import React from "react";
import { useFormikContext, Form } from "formik";
import { CountryList } from "../../../assets/staticData/CountryList";
import Button from "../../../ui-elements/Button/Button";
import { FormObserver } from "./FormObserver";

import Input from "../../../ui-elements/Input/Formik/Input";
import Checkbox from "../../../ui-elements/Input/Formik/Checkbox";
import { DatePickerField } from "../../../ui-elements/DatePicker/Formik/DatePicker";
import DropSelectSearch from "../../../ui-elements/DropSelect/DropSelectSearch/DropSelectSearch";
import { IPepWatchFormValues } from "../../../types/PepTypes";

import * as Yup from "yup";
import { isSSN } from "../../../services/utils";

export const PepWatchAddPersonForm: React.FC<{ values: IPepWatchFormValues; onClose?: () => void }> = ({ values, onClose }) => {
  const { setFieldValue, getFieldMeta } = useFormikContext<IPepWatchFormValues>();
  const countryCodeFieldMeta = getFieldMeta("countryCode");
  return (
    <Form>
      <FormObserver />
      <div className="form__body">
        <div className="form__row">
          <div className="form__col">
            <Checkbox
              label="Erlendur / ekki með íslenska kennitölu"
              name="foreign"
              id="foreign"
              value={values.foreign.toString()}
              onClick={() => {
              const willBeForeign = !values.foreign;
              setFieldValue("foreign", willBeForeign);
              setFieldValue("pep", !willBeForeign);
              setFieldValue("sanctions", willBeForeign);
            }}
            />
          </div>
        </div>

        {values.foreign === false ? (
          <div className="non-foreign-form">
            <div className="form__row">
              <div className="form__col">
                <Input label="Kennitala" name="ssn" id="ssn" value={values.ssn} />
              </div>
              <div className="form__col">
                <Input label="Nafn" name="name" id="name" value={values.name || ""} />
              </div>
            </div>
            <div className="form__row">
              <div className="form__col">
                <DropSelectSearch
                  usePortal={true}
                  error={
                    countryCodeFieldMeta.touched && countryCodeFieldMeta.error ? countryCodeFieldMeta.error : undefined
                  }
                  size="lg"
                  label="Land"
                  options={CountryList}
                  defaultValue={values.countryCode}
                  onChange={(cc: string) => setFieldValue("countryCode", cc)}
                />
              </div>
              <div className="form__col">
                <Input label="Tilvísun" name="reference" id="reference" value={values.reference || ""} />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="form__row">
              <div className="form__col">
                <Input label="Nafn" name="name" id="name" value={values.name || ""} />
              </div>
              <div className="form__col">
                <DatePickerField
                  withClearDateButton
                  max_width
                  dateFormat="yyyy-MM-dd"
                  value={values.dateOfBirth ?? ""}
                  label="Fæðingardagur"
                  name="dateOfBirth"
                  id="dateOfBirth"
                  placeholder="Veldu fæðingardag"
                  maxDate={new Date()}
                  showMonthDropdown
                  showYearDropdown
                  popperPlacement="bottom"
                />
              </div>
            </div>
            <div className="form__row">
              <div className="form__col">
                <DropSelectSearch
                  usePortal={true}
                  error={
                    countryCodeFieldMeta.touched && countryCodeFieldMeta.error ? countryCodeFieldMeta.error : undefined
                  }
                  size="lg"
                  label="Land"
                  options={CountryList}
                  defaultValue={values.countryCode}
                  onChange={(cc: string) => setFieldValue("countryCode", cc)}
                />
              </div>
              <div className="form__col">
                <Input label="Tilvísun" name="reference" id="reference" value={values.reference || ""} />
              </div>
            </div>
          </>
        )}
      </div>
      <div className="form__row">
        <div className="form__col">
          <div className="checkbox-wrapper">
            <Checkbox
              disabled={values.foreign}
              label="Vöktun á PEP lista"
              name="pep"
              id="pep"
              value={values.pep.toString()}
            />
            <Checkbox
              label="Vöktun á alþjóðlegum válistum"
              name="sanctions"
              id="sanctions"
              value={values.sanctions.toString()}
            />
          </div>
        </div>
      </div>
      <div className="form__actions">
        {onClose && (
          <Button type="button" buttonType="secondary" size="lg" onClick={onClose}>
            Hætta við
          </Button>
        )}
        <Button type="submit" buttonType="primary" size="lg">
          Staðfesta
        </Button>
      </div>
    </Form>
  );
};
export const validationSchema = Yup.object({
  name: Yup.string()
    .required("Nafn er nauðsynlegt")
    .test("name-parts", "Vinsamlegast sláðu inn bæði fornafn og eftirnafn", function (value) {
      if (!value) return false;
      const nameParts = value.trim().split(/\s+/);
      return nameParts.length >= 2;
    }),
  ssn: Yup.string().test("is-ssn", "Ógild kennitala", function (value) {
    // Skip validation if foreign is true
    if (this.parent.foreign === true) {
      return true;
    }
    // SSN is required when foreign is false
    if (!value) {
      return this.createError({ message: "Kennitala er nauðsynleg" });
    }
    return isSSN(value);
  }),
  countryCode: Yup.string()
    .required("Land er nauðsynlegt")
    .oneOf(
      CountryList.map((country) => country.value),
      "Vinsamlegast veldu land"
    ),
  dateOfBirth: Yup.mixed().test({
    name: "date-validation",
    test: function (value, context) {
      // If no value provided, it's not optional so return false
      if (!value) {
        return context.createError({
          message: "Dagsetning er nauðsynleg",
        });
      }
      // Check if date is valid
      let date;
      if (value instanceof Date) {
        date = value;
      } else if (typeof value === "string") {
        date = new Date(value);
      } else {
        return context.createError({
          message: "Ógild dagsetning",
        });
      }
      // Check if it's a valid date object
      if (isNaN(date.getTime())) {
        return context.createError({
          message: "Ógild dagsetning",
        });
      }
      // Check if the date is in the future
      if (date > new Date()) {
        return context.createError({
          message: "Dagsetning má ekki vera í framtíðinni",
        });
      }
      return true;
    },
  }),
  reference: Yup.string().optional()
});
